import React, { useEffect, useCallback } from "react";
import { Presentation, FolderOpen, Sparkles, X } from "lucide-react";
import { ModalLayout } from "../common/ModalLayout";

import { useSlidesForm } from "./hooks/useSlidesForm";
import { useSlidesStorage } from "./hooks/useSlidesStorage";
import { generateSlides } from "./api";
import { getSlideColor, getSlideBgColor } from "./utils";

import { FormStep } from "./steps/FormStep";
import { LoadingStep } from "./steps/LoadingStep";
import { ResultStep } from "./steps/ResultStep";
import { SavedStep } from "./steps/SavedStep";

import { createMaterial } from "../../../../lib/api";
import { useAuth } from "../../../../app/AuthContext";
import { useSchoolProfile } from "../../../../app/SchoolProfileContext";

import type { CreateSlidesAIProps, GeneratedSlides } from "./types";

export default function CreateSlidesAI({
  onClose = () => {},
  onSave,
  initialData,
  classes = [],
  subjects = [],
  isOpen = true,
}: CreateSlidesAIProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const { step, setStep, formData, updateFormData, resetForm, setInitialData } =
    useSlidesForm();

  const {
    savedPresentations,
    savePresentation,
    deletePresentation,
    loadPresentations,
  } = useSlidesStorage();

  const [generatedSlides, setGeneratedSlides] =
    React.useState<GeneratedSlides | null>(null);
  const [selectedSlide, setSelectedSlide] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    loadPresentations();
  }, [loadPresentations]);

  useEffect(() => {
    if (initialData) {
      setInitialData(initialData);
    }
  }, [initialData, setInitialData]);

  const handleGenerateSlides = useCallback(async () => {
    setStep("loading");
    setIsLoading(true);

    try {
      const slides = await generateSlides(formData);
      setGeneratedSlides(slides);
      setSelectedSlide(0);
      setStep("result");

      // Automatic saving in FilesManager
      if (token && activeProfile) {
        const payload = {
          title: slides.title,
          content: JSON.stringify({
            kind: "ai_slides",
            data: slides,
            topic: slides.topic,
            subject: slides.subject,
          }),
          type: "presentation",
          school_profile_id: activeProfile.id,
        };
        createMaterial(payload, token)
          .then(() => {
            console.log(
              "CreateSlidesAI: Material criado com sucesso, disparando evento...",
            );
            window.dispatchEvent(
              new CustomEvent("materialCreated", {
                detail: { type: "slides" },
              }),
            );
          })
          .catch(console.error);
      }
    } catch (error) {
      console.error("Erro ao gerar slides:", error);
      alert("Ocorreu um erro ao gerar os slides. Por favor, tente novamente.");
      setStep("form");
    } finally {
      setIsLoading(false);
    }
  }, [formData, setStep]);

  const handleSaveSlides = useCallback(async () => {
    if (!generatedSlides) return;

    try {
      const success = await savePresentation(generatedSlides);
      if (success) {
        alert("Apresentação salva com sucesso!");
        onSave?.(generatedSlides);
      } else {
        alert("Erro ao salvar a apresentação");
      }
    } catch (error) {
      alert("Erro ao salvar a apresentação");
    }
  }, [generatedSlides, savePresentation, onSave]);

  const handleLoadPresentation = useCallback(
    (pres: GeneratedSlides) => {
      setGeneratedSlides(pres);
      updateFormData("title", pres.title);
      updateFormData("subject", pres.subject);
      updateFormData("topic", pres.topic);
      updateFormData("audience", pres.audience);
      updateFormData("slideStyle", pres.slideStyle);
      updateFormData("colorScheme", pres.colorScheme);
      updateFormData("slideCount", pres.slideCount.toString() as any);
      setStep("result");
    },
    [setStep, updateFormData],
  );

  const handleDeletePresentation = useCallback(
    async (id: string) => {
      try {
        const success = await deletePresentation(id);
        if (success) {
          alert("Apresentação excluída!");
        }
      } catch (error) {
        alert("Erro ao excluir a apresentação");
      }
    },
    [deletePresentation],
  );

  const handleDownloadSlides = useCallback((format: string) => {
    console.log(`Baixando slides em formato: ${format}`);
    alert(`Download iniciado em formato ${format}`);
  }, []);

  const handleRegenerate = useCallback(() => {
    setStep("form");
    setGeneratedSlides(null);
    setSelectedSlide(0);
  }, [setStep]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title="Criar Slides com IA"
      subtitle="Apresentações profissionais em minutos"
      icon={
        <Presentation
          className="w-7 h-7"
          style={{ color: getSlideColor(formData.colorScheme) }}
        />
      }
    >
      <div className="flex flex-col h-full max-h-[calc(90vh-140px)]">
        <div className="px-6 py-2 border-b border-(--border-color) bg-(--bg-secondary) flex justify-end gap-2 shrink-0">
          <button
            onClick={() => setStep("saved")}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-(--bg-accent) rounded-lg transition text-sm text-(--text-secondary)"
          >
            <FolderOpen className="w-4 h-4" />
            <span>Salvos</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {step === "saved" && (
            <SavedStep
              savedPresentations={savedPresentations}
              onLoadPresentation={handleLoadPresentation}
              onDeletePresentation={handleDeletePresentation}
              onBack={() => setStep("form")}
            />
          )}

          {step === "form" && (
            <FormStep
              formData={formData}
              onFormChange={updateFormData}
              onGenerate={handleGenerateSlides}
              subjects={subjects}
              classes={classes}
              isLoading={isLoading}
            />
          )}

          {step === "loading" && (
            <LoadingStep colorScheme={formData.colorScheme} />
          )}

          {step === "result" && generatedSlides && (
            <ResultStep
              generatedSlides={generatedSlides}
              slideStyle={formData.slideStyle}
              colorScheme={formData.colorScheme}
              selectedSlide={selectedSlide}
              onSelectSlide={setSelectedSlide}
              onSave={handleSaveSlides}
              onRegenerate={handleRegenerate}
              onDownload={handleDownloadSlides}
            />
          )}
        </div>

        <div className="p-4 border-t border-(--border-color) flex items-center bg-(--bg-secondary) shrink-0">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: getSlideBgColor(formData.colorScheme),
              }}
            >
              <Sparkles
                className="w-4 h-4"
                style={{ color: getSlideColor(formData.colorScheme) }}
              />
            </div>
            <span className="text-xs text-(--text-tertiary) max-w-xs truncate md:max-w-none">
              {step === "form"
                ? "Forneça detalhes específicos para obter os melhores resultados!"
                : step === "loading"
                  ? "Processando sua solicitação..."
                  : "Sua apresentação está pronta! Revise e faça ajustes."}
            </span>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
